/**
 * Prasaar tokens — dark shell + light cards.
 *
 * Contrast rules (aimed at 40–60 year-old readers, not just WCAG AA):
 * - Text on white uses ink / inkMuted / brandInk (never bright teal).
 * - White text sits on brandFill / primary (never on bright teal).
 * - Bright teal is an accent on the dark shell only (bars, selected icons).
 */

export const tokens = {
  /** Text on light cards */
  ink: '#0E1218',
  inkMuted: '#3E4A56',
  /** Text on dark shell / nav */
  onShell: '#F2F4F7',
  onShellMuted: '#C5CCD6',
  /** Charcoal primary (on light surfaces) */
  primary: '#141A22',
  primaryDark: '#0A0D12',
  primaryLight: '#3A4450',
  primarySoft: '#E8ECF1',
  borderHover: '#8B95A3',
  mistDeep: '#121820',
  /** Bright teal — decoration on dark only */
  brand: '#2EC4B6',
  /** Teal fill that can carry white labels (~5.2:1) */
  brandFill: '#0E7A72',
  /** Teal used as text/links on white (~7.5:1) */
  brandInk: '#0B5F58',
  brandSoft: '#E8F3F2',
  signal: '#0B5F58',
  /** Light cards */
  surface: '#FFFFFF',
  canvas: '#0E1218',
  border: '#B8C0CC',
  danger: '#C01030',
  success: '#146C45',
  warning: '#B45309',
  onPrimary: '#FFFFFF',
  /** Dark atmosphere */
  shell: '#141A22',
  shellDeep: '#0A0D12',
  /** Aliases for login card */
  cardLight: '#FFFFFF',
  cardLightInk: '#0E1218',
  cardLightMuted: '#3E4A56',
};

/** Text styles for content sitting directly on the dark app shell (not inside cards). */
export const shellSx = {
  text: { color: tokens.onShell },
  textMuted: { color: tokens.onShellMuted },
  link: {
    color: tokens.onShell,
    fontWeight: 600,
    width: 'fit-content',
    '&:hover': { color: tokens.brand },
  },
};

/** Parse #RGB / #RRGGBB to { r, g, b }. */
function hexToRgb(hex) {
  let h = String(hex).replace('#', '').trim();
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const n = parseInt(h, 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

/** Alpha blend helper — use instead of hand-written rgba() with brand hexes. */
export function alpha(hex, opacity) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/** Shared dark-shell atmosphere (body + sticky header must match). */
export const shellBackdrop = {
  backgroundColor: tokens.canvas,
  backgroundImage: `
    radial-gradient(1000px 560px at 10% -10%, ${alpha(tokens.brand, 0.08)}, transparent 52%),
    radial-gradient(800px 480px at 90% 0%, ${alpha(tokens.brand, 0.04)}, transparent 48%),
    linear-gradient(165deg, ${tokens.shellDeep} 0%, ${tokens.shell} 48%, #121820 100%)
  `,
  backgroundAttachment: 'fixed',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
};

/** CSS custom properties mapped from tokens (injected via CssBaseline). */
export function cssVarMap() {
  return {
    '--ink': tokens.ink,
    '--ink-muted': tokens.inkMuted,
    '--on-shell': tokens.onShell,
    '--on-shell-muted': tokens.onShellMuted,
    '--primary': tokens.primary,
    '--primary-dark': tokens.primaryDark,
    '--primary-soft': tokens.primarySoft,
    '--brand': tokens.brand,
    '--brand-fill': tokens.brandFill,
    '--brand-ink': tokens.brandInk,
    '--brand-soft': tokens.brandSoft,
    '--signal': tokens.signal,
    '--surface': tokens.surface,
    '--canvas': tokens.canvas,
    '--border': tokens.border,
    '--danger': tokens.danger,
    '--success': tokens.success,
    '--warning': tokens.warning,
    '--mist': tokens.shell,
    '--mist-deep': tokens.mistDeep,
    '--accent': tokens.brand,
    '--accent-soft': tokens.brandSoft,
    '--on-primary': tokens.onPrimary,
    '--shell': tokens.shell,
    '--shell-deep': tokens.shellDeep,
  };
}
